/**
 * 🆕 [Phase 54] GanttGridCalendrier — refactor du rendu Gantt en CSS Grid.
 *
 * Remplace la structure actuelle de `CalendrierProduction.jsx` (div absolus
 * avec leftPct/widthPct calculés à la main) par un layout CSS Grid où :
 *   - 1 cellule = 1 jour (col 1..7)
 *   - 1 BT segment = N cellules adjacentes via grid-column-start/end
 *   - Chaque cellule peut être stylée indépendamment (overlay Sans chef,
 *     overlay Hors zone, etc.)
 *
 * Feature flag : `REACT_APP_USE_CALENDAR_CSS_GRID` (défaut: false).
 * Tant que `false`, l'ancien rendu reste utilisé — pas de régression possible.
 *
 * Status :
 *   ✅ D.1-D.2 : Composant MVP fonctionnel (1 semaine, BT mono-segment)
 *   ✅ D.3     : Branchement feature flag + plumbing CalendarWeekRow
 *   ✅ D.4a    : Multi-lanes (1 par chef ASC) + sub-rows packing + overlay Sans chef
 *   ✅ D.4b    : Carte BT complète — label hiérarchique + emojis par jour
 *                + animation punches actifs + badge Position Phase 51 BIS
 *   📋 D.4c+   : Tooltip Radix, drag-resize, tuile SC, RDV row, lavage row,
 *                animations passation, double-click jour
 *
 * Audit complet : voir `/app/memory/audit_calendar_refactor_phase54.md`.
 */
import { Fragment, useState, useRef, useEffect } from 'react';
import { computeBadgesParCellule } from '@/lib/calendarPhase51';
import { isJourFerieCCQ, isCongeCCQ } from '@/lib/ccq-holidays';
// 🆕 [v9 — 2026-06-12 Bug #1 P0] Découpage en runs calendaires strictement consécutifs.
//    Cf. /app/frontend/src/lib/calendarRuns.js
import { computeBTRuns } from '@/lib/calendarRuns';
// 🆕 [v9 — 2026-06-12 Bug #3 P2] Bus global pour avoir UN SEUL tooltip ouvert à la fois.
import { setActiveTooltipId, subscribeTooltipBus } from '@/lib/calendarTooltipBus';
import GanttBonTooltip from './GanttBonTooltip';


// 🆕 [Tâche 4 SESSION MAX] Re-export des helpers purs depuis @/lib/ganttHelpers
//    (API publique préservée pour CalendarWeekRow.jsx et autres consommateurs)
export {
  computeGridSpan,
  resolveCellColor,
  getGlobalLaneOrder,
  packBonsIntoSubRows,
  buildBonLabel,
  computeBonEmojiCells,
  getActivePunches,
  getCompletedPunches,
  isLastSegmentInBon,
  filterChefRdvs,
  getPositionBadge,
} from '@/lib/ganttHelpers';

// Import pour usage interne dans ce fichier (les usages locaux référencent
// les noms d'origine sans préfixe — re-export et import peuvent coexister en ES modules)
import {
  computeGridSpan,
  resolveCellColor,
  getGlobalLaneOrder,
  packBonsIntoSubRows,
  buildBonLabel,
  computeBonEmojiCells,
  getActivePunches,
  getCompletedPunches,
  isLastSegmentInBon,
  filterChefRdvs,
  getPositionBadge,
} from '@/lib/ganttHelpers';

export const USE_CALENDAR_CSS_GRID = process.env.REACT_APP_USE_CALENDAR_CSS_GRID === 'true';

/**
 * Helper pur — calcule la position grid d'un BT dans une semaine de 7 jours.
 * @returns {{ colStart, colEnd } | null} (1-indexed, colEnd exclusif). null si hors semaine.
 */









/**
 * 🆕 [Phase 54 D.4d] Helper pur — détecter un GAP VOLONTAIRE (≥1 jour ouvré
 * non férié / non congé CCQ) après ce segment et avant le prochain segment du BT.
 * Copie 1:1 de CalendarWeekRow.jsx L749-772.
 *
 * @returns {boolean}  true s'il existe ≥1 jour ouvré entre segLastDate et la date suivante du BT.
 */
export function detectWorkdayGapAfter(bon, segLastDate) {
  const bonDates = [...(bon.dates_chantier || [])].sort();
  const nextDateIndex = bonDates.indexOf(segLastDate) + 1;
  const nextDate = bonDates[nextDateIndex];
  if (!nextDate) return false;
  const current = new Date(segLastDate + 'T12:00:00');
  const next = new Date(nextDate + 'T12:00:00');
  let checkDate = new Date(current);
  checkDate.setDate(checkDate.getDate() + 1);
  while (checkDate < next) {
    const dateStr = checkDate.toISOString().split('T')[0];
    const dow = checkDate.getDay();
    if (dow >= 1 && dow <= 5 && !isJourFerieCCQ(dateStr) && !isCongeCCQ(dateStr)) {
      return true;  // gap ouvré détecté → poignée affichable
    }
    checkDate.setDate(checkDate.getDate() + 1);
  }
  return false;
}


/**
 * 🆕 [Phase 54 D.4f] Composant interne — rangée RDV au-dessus d'une lane chef.
 * Hauteur 18px, 7 cellules CSS Grid, badge par jour :
 *   - Si soumission ouverte (type_rdv='soumission' && !visite_effectuee && date >= today)
 *     → bouton orange cliquable (déclenche setRdvPopup pour fermer la visite)
 *   - Sinon → badge violet (titre du seul RDV ou count si plusieurs)
 *
 * Copie 1:1 de CalendarWeekRow.jsx L284-313 (sans le wrapper absolute legacy).
 */
function GanttRdvRow({ chefRdvs, weekDateStrs, setRdvPopup, laneKey }) {
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div
      className="grid grid-cols-7 gap-px mb-px"
      style={{ height: '18px' }}
      data-testid={`gantt-rdv-row-${laneKey}`}
    >
      {weekDateStrs.map((dayStr, di) => {
        const dayRdvs = chefRdvs.filter(ev => ev.date === dayStr);
        if (dayRdvs.length === 0) return <div key={di} />;
        const soumRdv = dayRdvs.find(
          e => e.type_rdv === 'soumission' && !e.visite_effectuee && e.date >= today
        );
        if (soumRdv) {
          return (
            <div key={di} className="flex items-center justify-center">
              <button
                type="button"
                className="bg-orange-500 text-white text-[9px] font-bold px-1.5 py-0 rounded-full truncate max-w-full cursor-pointer hover:bg-orange-600 transition-colors border border-orange-600"
                title={`Soumission: ${soumRdv.titre || 'RDV'} — Cliquer pour fermer la visite`}
                onClick={(e) => {
                  e.stopPropagation();
                  setRdvPopup && setRdvPopup(soumRdv);
                }}
                data-testid={`gantt-rdv-soum-${soumRdv.id}`}
              >
                {(soumRdv.titre || 'Soum.').substring(0, 8)}
              </button>
            </div>
          );
        }
        return (
          <div key={di} className="flex items-center justify-center">
            <div
              className="bg-violet-500 text-white text-[9px] font-bold px-1.5 py-0 rounded-full truncate max-w-full cursor-default"
              title={dayRdvs.map(e => `${e.heure_debut || ''} ${e.titre || 'RDV'}`).join(', ')}
              data-testid={`gantt-rdv-${laneKey}-${dayStr}`}
            >
              {dayRdvs.length === 1
                ? (dayRdvs[0].titre || 'RDV').substring(0, 8)
                : `${dayRdvs.length} RDV`}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * 🆕 [Phase 54 D.4f] Composant exporté — rangée Lavage en bas du week.
 * Hauteur 22px, CSS Grid 7 cols, 1 cellule cyan par route de lavage de la semaine.
 *
 * Copie 1:1 de CalendarWeekRow.jsx L849-875 (avec passage de absolute% → CSS Grid).
 */
export function GanttLavageLane({ routesLavage, weekStartDate, navigate }) {
  const dayMs = 24 * 60 * 60 * 1000;
  const weekDateStrs = [];
  for (let i = 0; i < 7; i++) {
    weekDateStrs.push(
      new Date(new Date(weekStartDate).getTime() + i * dayMs)
        .toISOString().slice(0, 10)
    );
  }
  const routesCeSemaine = (routesLavage || []).filter(r =>
    weekDateStrs.includes(r.date_route)
  );
  if (routesCeSemaine.length === 0) return null;
  return (
    <div
      className="grid grid-cols-7 gap-px mx-1 mb-1"
      style={{ height: '22px' }}
      data-testid="gantt-lavage-lane"
    >
      {routesCeSemaine.map(r => {
        const dayIdx = weekDateStrs.indexOf(r.date_route);
        if (dayIdx < 0) return null;
        const nbClients = r.clients?.length || 0;
        return (
          <div
            key={r.id}
            style={{
              gridColumnStart: dayIdx + 1,
              gridColumnEnd: dayIdx + 2,
              paddingRight: '2px',
            }}
          >
            <div
              onClick={() =>
                navigate && navigate('/routes-lavage', {
                  state: { selectedRouteId: r.id },
                })
              }
              className="rounded px-2 py-0.5 text-xs font-medium text-white truncate bg-cyan-500 cursor-pointer hover:bg-cyan-600 transition-colors"
              title={`Route de lavage le ${r.date_route} — ${nbClients} client(s)`}
              data-testid={`gantt-lavage-${r.id}`}
            >
              🚿 Lavage ({nbClients})
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * 🆕 [Phase 54 D.4d] Factory du handler onMouseDown du resize handle.
 * Copie 1:1 du legacy CalendarWeekRow.jsx L695-738 (mode 1 et mode 2 identiques).
 *
 * Attache listeners globaux `mousemove`/`mouseup` sur `document`, calcule
 * `daysDelta` via la largeur de la grille CSS Grid, met à jour le state
 * `dragging` du Context pour live preview, et appelle `handleExtendBon`
 * au mouseup si le drag est significatif (≥10px ET daysDelta ≠ 0).
 *
 * @returns {function} onMouseDown handler
 */
function createResizeHandler({ bon, segLastDate, laneEl, setDragging, handleExtendBon, activeRef }) {
  return (e) => {
    e.preventDefault();
    e.stopPropagation();
    // 🆕 [Phase 82 — 2026-06-22] Service Calls = mono-date par nature (champ
    // `date_prevue` dans le modèle), pas de drag-resize possible. Garde
    // défensive : early return si le bon est un Service Call.
    if (bon?.isServiceCall) return;
    if (!laneEl) return;
    const startX = e.clientX;
    const laneWidth = laneEl.getBoundingClientRect().width;
    const dayWidth = laneWidth / 7;
    let hasDragged = false;

    const onMouseMove = (ev) => {
      const delta = ev.clientX - startX;
      const daysDelta = Math.round(delta / dayWidth);
      if (Math.abs(delta) > 10) hasDragged = true;
      setDragging && setDragging({ bonId: bon.id, daysDelta });
    };
    const onMouseUp = (ev) => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      // 🆕 [D.5] Libérer la ref de cleanup — drag terminé normalement
      if (activeRef) activeRef.current = { move: null, up: null };
      const delta = ev.clientX - startX;
      const daysDelta = Math.round(delta / dayWidth);
      setDragging && setDragging(null);

      if (hasDragged && daysDelta !== 0) {
        const sortedDates = [...(bon.dates_chantier || [])].sort();
        const firstDate = sortedDates[0];
        if (segLastDate && firstDate) {
          const newEnd = new Date(segLastDate + 'T12:00:00');
          let remaining = Math.abs(daysDelta);
          const dir = daysDelta > 0 ? 1 : -1;
          while (remaining > 0) {
            newEnd.setDate(newEnd.getDate() + dir);
            const dow = newEnd.getDay();
            if (dow >= 1 && dow <= 5) remaining--;  // ne compte que les jours ouvrés
          }
          const newEndStr = newEnd.toISOString().split('T')[0];
          if (newEndStr >= firstDate) {
            handleExtendBon && handleExtendBon(bon, newEndStr);
          }
        }
      }
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    // 🆕 [D.5] Tracker les handlers actifs pour cleanup au démontage React
    if (activeRef) activeRef.current = { move: onMouseMove, up: onMouseUp };
  };
}

/**
 * 🆕 [Phase 54 D.4a-D.4b] Composant interne — rend UNE lane CSS Grid pour UN chef.
 *
 * D.4b ajoute pour chaque segment :
 *   - Bloc unique span N colonnes (au lieu de N cellules) avec couleur du segment
 *   - Row d'emojis par jour (24px) si dates_fixes / nacelle / équipements
 *   - Row de label (22px) : type ☁️/☀️, heure_debut, badge Position, label, badge punches
 *   - Overlays Sans chef par cellule (slate-400 70%) en pointer-events-none
 */
export function GanttGridLane({
  laneKey, chefBons, weekStartDate, presentsParJour, allBonsThisWeek, onBonClick,
  dossierCache = {}, punchesCache = {},
  navigate, openEditDatesDialog,
  dragging = null, setDragging, handleExtendBon,
  setShowServiceCallPunch,
  agendaEvents = [], showAgenda = true, setRdvPopup,
}) {
  const [hoveredBonId, setHoveredBonId] = useState(null);
  // 🆕 [v10 — 2026-06-12 Bug #4 P2] Track aussi quel SEGMENT est hovered pour
  // permettre l'affichage du tooltip sur n'importe quel run du BT (Eliot :
  // "Tu fais seulement apparaître le tooltip sur le PREMIER segment. Si un BT
  // est coupé en plusieurs segments, je ne vois pas la fenêtre sur le 2e, 3e
  // segment etc."). Format : "{bon.id}-s{segIdx}". Le tooltip est rendu sur
  // le segment qui matche cette clé — un seul DOM tooltip par BT.
  const [hoveredSegKey, setHoveredSegKey] = useState(null);
  // 🆕 [2026-06-11] Fix UX tooltip "trop sensible" — délai de fermeture 250ms
  // qui s'annule si la souris ré-entre sur le BT OU sur le tooltip. Permet à
  // l'utilisatrice de traverser le gap (4px mt-1) entre le segment et la
  // popup pour cliquer sur "Voir le bon" / "Modifier dates".
  const hoverCloseTimerRef = useRef(null);
  // 🆕 [v9 — 2026-06-12 Bug #3] Délai d'ouverture 500ms (au lieu de 0ms instantané)
  //   pour éviter les déclenchements accidentels au survol rapide.
  const hoverOpenTimerRef = useRef(null);
  const cancelHoverClose = () => {
    if (hoverCloseTimerRef.current) {
      clearTimeout(hoverCloseTimerRef.current);
      hoverCloseTimerRef.current = null;
    }
  };
  const cancelHoverOpen = () => {
    if (hoverOpenTimerRef.current) {
      clearTimeout(hoverOpenTimerRef.current);
      hoverOpenTimerRef.current = null;
    }
  };
  const scheduleHoverClose = () => {
    cancelHoverOpen();
    cancelHoverClose();
    hoverCloseTimerRef.current = setTimeout(() => {
      setHoveredBonId(null);
      setHoveredSegKey(null);
      setActiveTooltipId(null);
      hoverCloseTimerRef.current = null;
    }, 250);
  };
  const openHover = (bonId, segIdx = 0) => {
    cancelHoverClose();
    cancelHoverOpen();
    // 🆕 [v9 — 2026-06-12 Bug #3] Attendre 500ms avant de réellement ouvrir.
    hoverOpenTimerRef.current = setTimeout(() => {
      // Publier sur le bus → les autres instances ferment leur tooltip
      setActiveTooltipId(bonId);
      setHoveredBonId(bonId);
      // 🆕 [v10 — 2026-06-12 Bug #4] Track le segment hovered pour positionner
      // le tooltip sur le bon run (et pas systématiquement le 1er).
      setHoveredSegKey(`${bonId}-s${segIdx}`);
      hoverOpenTimerRef.current = null;
    }, 500);
  };
  // 🆕 [v9 — 2026-06-12 Bug #3] Abonnement au bus global : si un autre
  // GanttGridRow ouvre son tooltip, on ferme immédiatement le nôtre.
  useEffect(() => {
    const unsub = subscribeTooltipBus((activeId) => {
      if (activeId !== hoveredBonId && hoveredBonId !== null) {
        cancelHoverOpen();
        cancelHoverClose();
        setHoveredBonId(null);
        setHoveredSegKey(null);
      }
    });
    return unsub;
  }, [hoveredBonId]);
  // Cleanup : annuler les timers au démontage (évite setState sur composant démonté)
  useEffect(() => {
    return () => {
      cancelHoverOpen();
      cancelHoverClose();
    };
  }, []);
  // 🆕 [D.5] Tracker les handlers drag actifs pour cleanup au démontage React
  //   (edge case rare : composant unmount pendant un drag — sans ce cleanup,
  //   les listeners document.mousemove/mouseup deviendraient orphelins et
  //   continueraient à appeler setDragging sur un composant démonté).
  const activeDragRef = useRef({ move: null, up: null });
  useEffect(() => {
    return () => {
      const { move, up } = activeDragRef.current;
      if (move) document.removeEventListener('mousemove', move);
      if (up) document.removeEventListener('mouseup', up);
    };
  }, []);
  const cellMap = computeBadgesParCellule(allBonsThisWeek);
  const subRows = packBonsIntoSubRows(chefBons);
  const nbSubRows = Math.max(1, subRows.length);
  const dayMs = 24 * 60 * 60 * 1000;

  // 🆕 [D.4f] Calcul de la liste des dates de la semaine + filtrage RDV chef
  const weekDateStrsLane = [];
  for (let i = 0; i < 7; i++) {
    weekDateStrsLane.push(
      new Date(new Date(weekStartDate).getTime() + i * dayMs)
        .toISOString().slice(0, 10)
    );
  }
  const chefRdvs = showAgenda
    ? filterChefRdvs(agendaEvents, laneKey, weekDateStrsLane)
    : [];
  const hasRdvs = chefRdvs.length > 0;

  // 🚀 [Phase v13.50 — 2026-07-14] Lane virtuelle « Sans chef assigné » pour les
  //   BT approximatifs sans chef. Style visuel distinct (bg-gray-50 + testid).
  const isLaneNonAssigne = laneKey === '__non_assigne__';

  return (
    <div
      data-testid={`gantt-lane-wrapper-${laneKey}`}
      {...(isLaneNonAssigne
        ? {
          'data-testid-non-assigne': 'calendrier-row-non-assigne',
          className: 'bg-gray-50/70 border-l-2 border-gray-300',
          title: '🚫 Sans chef assigné — planifications approximatives à assigner',
        }
        : {})}
    >
      {/* 🚀 [Phase v13.50 — 2026-07-14] Marqueur `calendrier-row-non-assigne`
          (via un second data-testid explicite pour compat testing tool). */}
      {isLaneNonAssigne && (
        <div
          data-testid="calendrier-row-non-assigne"
          className="text-[10px] font-semibold text-gray-500 px-2 pt-1 pb-0.5 flex items-center gap-1"
        >
          🚫 Sans chef assigné
        </div>
      )}
      {/* 🆕 [D.4f] Rangée RDV au-dessus de la lane (si chef invité/owner sur ≥1 jour) */}
      {hasRdvs && (
        <GanttRdvRow
          chefRdvs={chefRdvs}
          weekDateStrs={weekDateStrsLane}
          setRdvPopup={setRdvPopup}
          laneKey={laneKey}
        />
      )}
      <div
        className="grid grid-cols-7 gap-px relative"
        style={{
          gridTemplateRows: `repeat(${nbSubRows}, minmax(26px, auto))`,
          marginBottom: '2px',
        }}
        data-testid={`gantt-grid-lane-${laneKey}`}
      >
      {subRows.map((rowBons, rowIdx) =>
        rowBons.map(bon => {
          // 🐛 FIX [v9 — 2026-06-12 Bug #1 P0] Le fallback créait UN seul segment
          //   couvrant `[dates[0], dates[last]]` → traversait visuellement tous
          //   les trous (ex. BT Julie Fournier : 1-5, 8-9, 11, 16 juin → une
          //   bande continue qui couvrait aussi 6,7,10,12-15). Désormais on
          //   utilise `computeBTRuns` qui découpe en runs strictement
          //   consécutifs (diff calendaire = 1). Chaque run devient une bande
          //   Gantt distincte sur la même lane chef ; les jours non sélectionnés
          //   sont automatiquement des trous (fix aussi Bug #2 weekend).
          const segments = (Array.isArray(bon.segments) && bon.segments.length > 0)
            ? bon.segments
            : computeBTRuns(bon.dates_chantier || []).map(run => ({
                firstDate: run.firstDate,
                lastDate: run.lastDate,
                chefId: bon.chef_equipe_id,
                color: bon.color || '#3b82f6',
              }));

          return segments.map((seg, segIdx) => {
            const span = computeGridSpan(seg.firstDate, seg.lastDate, weekStartDate);
            if (!span) return null;
            // Construire la liste des dates couvertes par ce segment
            const spanDates = [];
            for (let col = span.colStart; col < span.colEnd; col++) {
              spanDates.push(
                new Date(new Date(weekStartDate).getTime() + (col - 1) * dayMs)
                  .toISOString().slice(0, 10)
              );
            }
            const segColor = seg.color || bon.color || '#3b82f6';
            // Mini-BT pour resolveCellColor (overlay Sans chef par cellule)
            const segBt = {
              chef_equipe_id: seg.chefId || bon.chef_equipe_id,
              chefs_par_jour: bon.chefs_par_jour,
              color_par_chef: bon.color_par_chef,
              color: segColor,
            };

            // Helpers D.4b
            const label = buildBonLabel(bon, dossierCache);
            const isInt = bon.type_travaux === 'interieur';
            const { emojiCells, hasAnyEmojis } = computeBonEmojiCells(bon, spanDates);
            const activePunches = getActivePunches(bon, punchesCache);
            const teamId = seg.chefId || bon.chef_equipe_id || bon.team_id;
            const positionBadge = getPositionBadge(bon, teamId, spanDates, cellMap);
            const minBlockHeight = hasAnyEmojis ? 50 : 26;

            // 🆕 [D.4d] Live preview drag-resize : ajustement gridColumnEnd
            //   - Dragging actif sur CE bon → daysDelta vient du Context
            //   - Cap entre [colStart+1, 8] pour rester dans la semaine
            const isDraggingThis = dragging?.bonId === bon.id;
            const dragDays = isDraggingThis ? (dragging.daysDelta || 0) : 0;
            const previewColEnd = Math.max(
              span.colStart + 1,
              Math.min(8, span.colEnd + dragDays)
            );

            // 🆕 [D.4d] Détermination si la poignée de resize doit s'afficher
            //   - Mode 1 : ce segment est le DERNIER du BT (toujours redimensionnable)
            //   - Mode 2 : segment intermédiaire avec GAP VOLONTAIRE ouvré après
            const segLastDate = spanDates[spanDates.length - 1];
            const showResizeHandle = segLastDate && (
              isLastSegmentInBon(bon, segLastDate) ||
              detectWorkdayGapAfter(bon, segLastDate)
            );

            return (
              <Fragment key={`${bon.id}-s${segIdx}-r${rowIdx}`}>
                {/* Wrapper relative du segment — héberge le button + le tooltip absolu */}
                <div
                  className="relative"
                  style={{
                    gridColumnStart: span.colStart,
                    gridColumnEnd: previewColEnd,
                    gridRowStart: rowIdx + 1,
                    gridRowEnd: rowIdx + 2,
                    transition: isDraggingThis ? 'none' : 'grid-column-end 0.2s',
                  }}
                  onMouseEnter={() => openHover(bon.id, segIdx)}
                  onMouseLeave={scheduleHoverClose}
                >
                  {/* Bloc principal du segment.
                      🆕 [D.4e] Passation chef visuelle (legacy CWR L427-455) :
                        - bordure pointillée blanche à gauche (3px dashed)
                        - boxShadow inset 4px sur couleur du chef PRÉCÉDENT (colorPrec)
                        - title spécifique « Passation : reprise de {chefNomPrec} »
                      🆕 [D.4e] Service Call : click ouvre la dialog de pointage SC
                        (au lieu de handleBarClick), title spécifique. */}
                  <button
                    type="button"
                    onClick={() => {
                      if (bon.isServiceCall && setShowServiceCallPunch) {
                        setShowServiceCallPunch(bon);
                      } else if (onBonClick) {
                        onBonClick(bon, spanDates[0]);
                      }
                    }}
                    className="relative rounded overflow-hidden cursor-pointer hover:opacity-90 transition-opacity flex flex-col w-full h-full"
                    style={{
                      // 🚀 [Phase v13.46.6 — 2026-07-18] Fond retiré du <button> :
                      //   le fond est désormais peint cellule par cellule via
                      //   <GanttBarBackground/> ci-dessous (une <div> par jour
                      //   du segment, couleur résolue par `resolveCellColor`).
                      //   Fallback `segColor` conservé UNIQUEMENT en cas de
                      //   `spanDates` vide (défensif).
                      backgroundColor: spanDates.length === 0 ? segColor : undefined,
                      minHeight: `${minBlockHeight}px`,
                      borderLeft: bon.isPassationDepuis
                        ? '3px dashed rgba(255,255,255,0.95)'
                        : undefined,
                      boxShadow: bon.isPassationDepuis
                        ? `inset 4px 0 0 0 ${bon.isPassationDepuis.colorPrec || 'rgba(0,0,0,0.25)'}`
                        : undefined,
                      // 🚀 [Phase v13.50 — 2026-07-14] Rendu shadow approximatif :
                      //   opacité 0.55 + bordure pointillée pour différencier du BT officiel.
                      //   Appliqué sur le CONTAINER (button) → l'ensemble des
                      //   cellules multi-couleurs devient pâle uniformément.
                      ...(bon.type_planification === 'approximative' && {
                        opacity: 0.55,
                        border: '1px dashed rgba(0,0,0,0.45)',
                      }),
                    }}
                    data-testid={`gantt-bar-${bon.id}-s${segIdx}`}
                    data-segment-idx={segIdx}
                    data-service-call={bon.isServiceCall ? '1' : '0'}
                    data-passation={bon.isPassationDepuis ? '1' : '0'}
                    data-type-planification={bon.type_planification || 'officielle'}
                    title={
                      bon.type_planification === 'approximative'
                        ? (() => {
                            const v = bon.planif_approx_voeu || {};
                            const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin',
                                          'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
                            const PORTIONS = { debut: 'Début', milieu: 'Milieu', fin: 'Fin' };
                            const nJ = v.duree_jours_ouvrables || 0;
                            const sfx = nJ > 1 ? 's' : '';
                            return `Planification approximative — ${PORTIONS[v.portion] || ''} ${MOIS[(v.mois || 1) - 1] || ''} ${v.annee || ''} — ${nJ} jour${sfx} ouvrable${sfx}`;
                          })()
                        : bon.isPassationDepuis
                          ? `Passation : reprise de ${bon.isPassationDepuis.chefNomPrec || 'chef précédent'}`
                          : bon.isServiceCall
                            ? 'Cliquez pour gérer le pointage du Service Call'
                            : `${label} — ${spanDates[0]}${spanDates.length > 1 ? `..${spanDates[spanDates.length - 1]}` : ''}`
                    }
                  >
                  {/* 🚀 [Phase v13.46.6 — 2026-07-18] Fond multi-couleurs par jour.
                        Chaque cellule (un jour) prend la couleur du chef effectif via
                        `resolveCellColor(segBt, cellDate, presentsParJour)` — gère
                        nativement `chefs_par_jour[date] = chefRemplacantId` (couleur
                        du remplaçant) et `chefs_par_jour[date] = '__none__'` ou chef
                        pas cédulé ce jour (gris #94a3b8 + label « Sans chef »).
                        L'ancien overlay séparé « Sans chef » (Phase 51 BIS B) est
                        supprimé — les cellules grises sont natives. */}
                  <div
                    className="absolute inset-0 flex pointer-events-none"
                    aria-hidden="true"
                    data-testid={`gantt-bar-bg-${bon.id}-s${segIdx}`}
                  >
                    {spanDates.map((cellDate, ci) => {
                      const cellInfo = resolveCellColor(segBt, cellDate, presentsParJour);
                      const isFirst = ci === 0;
                      const isLast = ci === spanDates.length - 1;
                      return (
                        <div
                          key={cellDate}
                          className={`flex-1 relative ${isFirst ? 'rounded-l' : ''} ${isLast ? 'rounded-r' : ''}`}
                          style={{ backgroundColor: cellInfo.color }}
                          data-testid={`gantt-cell-${bon.id}-${cellDate}`}
                          data-sans-chef={cellInfo.isSansChef ? '1' : '0'}
                        >
                          {cellInfo.isSansChef && (
                            <span
                              className="absolute top-0 right-0 text-[8px] bg-slate-700/80 text-white rounded-bl px-0.5"
                              data-testid={`gantt-cell-sanschef-label-${bon.id}-${cellDate}`}
                            >
                              Sans chef
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {/* Contenu du bloc (emojis + label + badges) rendu AU-DESSUS
                        du fond multi-couleurs via `relative z-10`. */}
                  <div className="relative z-10 flex flex-col w-full h-full pointer-events-none">
                  {/* Row 1 : Emojis par jour (height 24px) — si applicable */}
                  {hasAnyEmojis && (
                    <div className="flex" style={{ height: '24px' }}>
                      {emojiCells.map((emojis, ci) => (
                        <div
                          key={ci}
                          className="flex-1 flex items-center justify-center text-base gap-0.5"
                        >
                          {ci === 0 && (
                            <span
                              className="text-xs"
                              title={isInt ? 'Intérieur' : 'Extérieur'}
                            >
                              {isInt ? '☁️' : '☀️'}
                            </span>
                          )}
                          {emojis.map((em, ei) => <span key={ei}>{em}</span>)}
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Row 2 : Type + heure + badge Position + label + punches */}
                  <div
                    className="px-2 py-1 text-xs font-semibold text-white truncate flex items-center gap-1"
                    style={{ minHeight: '22px' }}
                  >
                    {!hasAnyEmojis && (
                      <span
                        className="shrink-0"
                        title={isInt ? 'Intérieur' : 'Extérieur'}
                        aria-label={isInt ? 'Intérieur' : 'Extérieur'}
                      >
                        {isInt ? '☁️' : '☀️'}
                      </span>
                    )}
                    {bon.heure_debut && (
                      <span
                        className="shrink-0 text-[10px] font-semibold bg-white/20 rounded px-1"
                        title="Heure début prévue"
                      >
                        {bon.heure_debut}
                      </span>
                    )}
                    {/* 🆕 [Phase 82 — 2026-06-22] Badge visuel pour les Service Calls. */}
                    {bon.isServiceCall && (
                      <span
                        className="shrink-0 text-[10px] font-bold bg-white/90 text-amber-700 rounded px-1.5 py-0.5"
                        title="Service Call"
                        data-testid={`gantt-badge-sc-${bon.id}`}
                      >
                        🔧 SC
                      </span>
                    )}
                    {positionBadge && (
                      <span
                        className={`shrink-0 text-[10px] font-bold rounded-full px-1.5 ${positionBadge.position === 0 ? 'bg-emerald-500 text-white' : 'bg-orange-400 text-white'}`}
                        title={`Position ${positionBadge.position + 1} le ${positionBadge.date} (équipe ${teamId})`}
                        data-testid={`gantt-badge-position-${bon.id}-s${segIdx}`}
                      >
                        Position {positionBadge.position + 1}
                      </span>
                    )}
                    <span className="truncate" title={label}>{label}</span>
                    {activePunches.length > 0 && (
                      <span
                        className="flex items-center gap-0.5 bg-green-500 text-white text-[10px] px-1.5 py-0.5 rounded-full shrink-0"
                        data-testid={`gantt-punch-active-${bon.id}-s${segIdx}`}
                        title={`${activePunches.length} employé(s) actif(s)`}
                      >
                        <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                        {activePunches.length}
                      </span>
                    )}
                  </div>
                  </div>
                </button>
                  {/* 🆕 [D.4d] Poignée de resize HTML5 — affichée si
                      - isLastSegment (mode 1) : toujours sur dernier segment du BT
                      - OU detectWorkdayGapAfter (mode 2) : segment intermédiaire avec gap ouvré
                      🆕 [Phase 82 — 2026-06-22] Pas de resize sur Service Call (mono-date par nature). */}
                  {showResizeHandle && !bon.isServiceCall && (
                    <div
                      className="absolute right-0 top-0 bottom-0 w-4 cursor-col-resize flex items-center justify-center hover:bg-white/50 transition-all rounded-r"
                      style={{ background: 'rgba(255,255,255,0.3)' }}
                      title="Glisser pour modifier la durée"
                      data-testid={`gantt-resize-handle-${bon.id}-s${segIdx}`}
                      onMouseDown={(e) => {
                        // Référence du grid container (lane) pour calcul dayWidth
                        const laneEl = e.currentTarget.closest(
                          `[data-testid="gantt-grid-lane-${laneKey}"]`
                        );
                        const handler = createResizeHandler({
                          bon, segLastDate, laneEl, setDragging, handleExtendBon,
                          activeRef: activeDragRef,
                        });
                        handler(e);
                      }}
                    >
                      <div className="w-1.5 h-6 bg-white/80 rounded-full" />
                    </div>
                  )}
                  {/* 🆕 [D.4c] Tooltip riche au hover (state local hoveredBonId)
                      🆕 [2026-06-11] Tooltip reçoit aussi cancelHoverClose / scheduleHoverClose
                          pour que le survol DU tooltip annule la fermeture
                      🆕 [v10 — 2026-06-12 Bug #4] Tooltip rendu sur le segment
                          actuellement HOVERED (matche `hoveredSegKey`), pas
                          systématiquement sur segIdx===0. Permet d'avoir le
                          tooltip sur n'importe quel run du BT. Garantit qu'UNE
                          SEULE instance DOM est rendue par BT (anti-duplication). */}
                  {hoveredBonId === bon.id && hoveredSegKey === `${bon.id}-s${segIdx}` && (
                    <GanttBonTooltip
                      bon={bon}
                      dossier={dossierCache[bon.dossier_id]}
                      activePunches={activePunches}
                      completedPunches={getCompletedPunches(bon, punchesCache)}
                      navigate={navigate}
                      openEditDatesDialog={openEditDatesDialog}
                      onMouseEnter={cancelHoverClose}
                      onMouseLeave={scheduleHoverClose}
                    />
                  )}
                </div>
                {/* 🚀 [Phase v13.46.6 — 2026-07-18] Overlay séparé « Sans chef »
                    SUPPRIMÉ. Le rendu multi-couleurs cellule par cellule (fond
                    du <button> ci-dessus via `resolveCellColor`) gère nativement
                    les cellules « Sans chef » (couleur gris #94a3b8 + label).
                    Testid legacy `gantt-sanschef-*` remplacé par
                    `gantt-cell-{id}-{date}` avec `data-sans-chef="1"` sur les
                    cellules concernées. */}
              </Fragment>
            );
          });
        })
      )}
      </div>
    </div>
  );
}

/**
 * Composant principal — rend une rangée Gantt en CSS Grid (1 lane par chef).
 *
 * Props :
 *   - bons             : weekBons (post-segmentation) à afficher
 *   - weekStartDate    : lundi de la semaine
 *   - presentsParJour  : {date: [emp_ids]} (Phase 51 BIS B — overlay Sans chef)
 *   - onBonClick(bon, cellDate)
 *   - allBonsThisWeek  : pour calcul des badges Position (Phase 51 BIS A)
 *   - allBons          : tous les BT du state global (ordre stable des lanes)
 *   - dossierCache     : 🆕 [D.4b] pour résolution clientNom/nomProjet
 *   - punchesCache     : 🆕 [D.4b] pour détection punches actifs (animate-pulse)
 *   - employees        : 🆕 [D.4a] réservé D.4c+ (tooltip riche)
 */
export function GanttGridRow({
  bons, weekStartDate, presentsParJour = {}, onBonClick,
  allBonsThisWeek = [], allBons = [],
  dossierCache = {}, punchesCache = {},
  navigate, openEditDatesDialog,
  dragging = null, setDragging, handleExtendBon,
  setShowServiceCallPunch,
  agendaEvents = [], showAgenda = true, setRdvPopup,
  routesLavage = [],
  // employees réservé (résolution noms chef côté futures features)
  employees: _employees = [],
}) {
  const laneOrder = getGlobalLaneOrder(
    allBons.length > 0 ? allBons : allBonsThisWeek,
    bons,
  );
  const lanes = {};
  (bons || []).forEach(bon => {
    const key = bon.chef_equipe_id || '_none';
    if (!lanes[key]) lanes[key] = [];
    lanes[key].push(bon);
  });
  // 🚀 [Phase v13.50 — 2026-07-14] Injecter la lane `__non_assigne__` en TÊTE
  //   si des BT approx sans chef sont présents ce week-là (Sujet 4.D).
  const NON_ASSIGNE = '__non_assigne__';
  const hasNonAssigne = Object.prototype.hasOwnProperty.call(lanes, NON_ASSIGNE);
  const laneOrderFinal = hasNonAssigne
    ? [NON_ASSIGNE, ...laneOrder.filter(l => l !== NON_ASSIGNE)]
    : laneOrder;
  return (
    <div className="flex flex-col px-1 pb-1" data-testid="gantt-grid-row">
      {laneOrderFinal.map(laneKey => (
        <GanttGridLane
          key={laneKey}
          laneKey={laneKey}
          chefBons={lanes[laneKey] || []}
          weekStartDate={weekStartDate}
          presentsParJour={presentsParJour}
          allBonsThisWeek={allBonsThisWeek}
          onBonClick={onBonClick}
          dossierCache={dossierCache}
          punchesCache={punchesCache}
          navigate={navigate}
          openEditDatesDialog={openEditDatesDialog}
          dragging={dragging}
          setDragging={setDragging}
          handleExtendBon={handleExtendBon}
          setShowServiceCallPunch={setShowServiceCallPunch}
          agendaEvents={agendaEvents}
          showAgenda={showAgenda}
          setRdvPopup={setRdvPopup}
        />
      ))}
      {/* 🆕 [D.4f] Rangée Lavage en bas du week (toujours visible si routes ce week-là) */}
      <GanttLavageLane
        routesLavage={routesLavage}
        weekStartDate={weekStartDate}
        navigate={navigate}
      />
    </div>
  );
}

export default GanttGridRow;
